import { Icon, useTranslation, V2 } from '@testsigma/atoms';
import classNames from 'classnames';
import React from 'react';
import * as Sentry from '@sentry/react';
import { FilterOperation } from '@enums/filter.operation.enum';
import { FilterQuery } from '@models/filter-query.model';

const { PageLoader } = V2;

type DocumentURLProps =
  | {
      path: string;
      label: string;
      children?: never;
      className?: string;
      showArrowIcon?: boolean;
      isFullPath?: boolean;
    }
  | {
      path: string;
      label?: never;
      children: React.ReactNode;
      className?: string;
      showArrowIcon?: boolean;
      isFullPath?: boolean;
    };

export const DocumentURL = (props: DocumentURLProps) => {
  const {
    path,
    label,
    className,
    children,
    showArrowIcon = false,
    isFullPath = false,
  } = props;
  const DOCUMENT_URL_HOST = !isFullPath
    ? import.meta.env.VITE_DOCUMENT_URL_HOST
    : '';
  const { t } = useTranslation();
  return (
    <a
      href={`${DOCUMENT_URL_HOST}${path}`}
      target='_blank'
      rel='noreferrer'
      className={classNames('px-1 hover:underline text-primary-600', className)}
    >
      {children ? children : t(label)}
      {showArrowIcon && (
        <Icon
          className='text-primary-500 shrink-0 font-semibold rotate-180 text-lg'
          name={'arrow-back'}
        />
      )}
    </a>
  );
};

export const getDocumentURL = (path: string) => {
  const DOCUMENT_URL_HOST = import.meta.env.VITE_DOCUMENT_URL_HOST;
  return `${DOCUMENT_URL_HOST}${path}`;
};

export { PageLoader };

export const logErrorToSentry = ({
  error,
  extraInfo,
}: {
  error: any;
  extraInfo?: any;
}) => {
  Sentry?.captureException(error, { extra: extraInfo });
};

export function dataurltoFile(
  dataUrl: string,
  filename: string = 'file',
): File {
  const blob = dataurltoBlob(dataUrl);
  return new File([blob], filename, { type: blob.type });
}
function dataurltoBlob(dataUrl: string): Blob {
  const byteString = atob(dataUrl.split(',')[1]);
  const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

export function fromBase64(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function generateFilterQuerySting(filterQueries: FilterQuery[]) {
  const filterQueryStrings = [];

  if (filterQueries?.length) {
    filterQueries.forEach((filterQuery) => {
      if (
        filterQuery.key?.toLowerCase()?.includes?.('date') ||
        filterQuery.key?.toLowerCase()?.includes?.('runon')
      ) {
        if (filterQuery.operation == FilterOperation.GREATER_THAN) {
          filterQueryStrings.push(`${filterQuery.key}>${filterQuery.value}`);
        } else if (filterQuery.operation == FilterOperation.LESS_THAN) {
          filterQueryStrings.push(`${filterQuery.key}<${filterQuery.value}`);
        }
      } else if (filterQuery.operation == FilterOperation.GREATER_THAN) {
        filterQueryStrings.push(
          `${filterQuery.key}>${
            typeof filterQuery.value === 'boolean' ? '0' : filterQuery.value
          }`,
        );
      } else if (filterQuery.operation === FilterOperation.IN) {
        const isValueArray = Array.isArray(filterQuery.value);
        filterQueryStrings.push(
          `${filterQuery.key}@${
            isValueArray ? filterQuery.value?.join?.('#') : filterQuery.value
          }`,
        );
      } else if (
        filterQuery.operation === FilterOperation.EQUALITY &&
        filterQuery.key === 'usageCount'
      ) {
        filterQueryStrings.push(`${filterQuery.key}:0`);
      } else if (filterQuery.operation === FilterOperation.EQUALITY) {
        filterQueryStrings.push(`${filterQuery.key}:${filterQuery.value}`);
      }
    });
  }
  return filterQueryStrings;
}

export const isPreview = () => window.location.pathname.includes('/preview');

export const isMobileDebugger = () =>
  window.location.pathname.includes('/debugrun');

export const isMobileRecorder = () =>
  window.location.pathname.includes('/record');

/**
 * Opens a URL in a new tab by programmatically creating and clicking an anchor element
 * @param url - The URL to open
 */
export const openUrlInNewTab = (url: string) => {
  if (!url) return;

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
